import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Zap, Flame, Trophy, BookOpen, Brain, ChevronRight, Star, Search, Filter, Clock, Trash2, CalendarDays } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import GoalsWidget from "@/components/GoalsWidget";
import ShareProgress from "@/components/ShareProgress";
import ScholarAvatar from "@/components/ScholarAvatar";
import AvatarTimeline from "@/components/AvatarTimeline";

const LEVEL_THRESHOLDS = [0, 100, 300, 600, 1000, 1500, 2200, 3000, 4000, 5500];

function getLevelProgress(xp) {
  let level = 1;
  for (let i = 0; i < LEVEL_THRESHOLDS.length; i++) {
    if (xp >= LEVEL_THRESHOLDS[i]) level = i + 1;
    else break;
  }
  const currentThreshold = LEVEL_THRESHOLDS[level - 1] || 0;
  const nextThreshold = LEVEL_THRESHOLDS[level] || LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1] + 1000;
  const progress = ((xp - currentThreshold) / (nextThreshold - currentThreshold)) * 100;
  return { level, progress: Math.min(progress, 100), xpToNext: nextThreshold - xp };
}

const ICON_MAP = {
  BookOpen, Trophy, Brain, Zap, Flame, Star,
  GraduationCap: Trophy,
  Highlighter: Star,
};

export default function Dashboard({ api }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [reviewStats, setReviewStats] = useState(null);

  const fetchData = async (search = "", paperType = "") => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (paperType) params.append("paper_type", paperType);
      const [statsRes, papersRes, reviewRes] = await Promise.all([
        axios.get(`${api}/stats`, { withCredentials: true }),
        axios.get(`${api}/papers?${params.toString()}`, { withCredentials: true }),
        axios.get(`${api}/reviews/stats`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setPapers(papersRes.data);
      setReviewStats(reviewRes.data);
    } catch (e) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(searchQuery, typeFilter);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery, typeFilter]);

  const handleDeletePaper = async (paperId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${api}/papers/${paperId}`, { withCredentials: true });
      setPapers((prev) => prev.filter((p) => p.id !== paperId));
      toast.success("Paper deleted");
    } catch {
      toast.error("Failed to delete paper");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="dashboard-loading">
        <div className="neo-card text-center p-8">
          <div className="w-12 h-12 border-4 border-neo-yellow border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-heading font-bold text-lg">Loading your quest...</p>
        </div>
      </div>
    );
  }

  const levelInfo = getLevelProgress(stats?.total_xp || 0);
  const inProgressPapers = papers.filter((p) => p.status === "in_progress");
  const badges = stats?.badges || [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Hero Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 lg:gap-6">
        {/* Avatar + XP Card */}
        <div className="sm:col-span-2 lg:col-span-5 neo-card">
          <div className="flex items-center gap-4">
            <ScholarAvatar level={levelInfo.level} size={80} showLabel={true} />
            <div className="flex-1 min-w-0">
              <p className="text-sm uppercase tracking-[0.15em] text-neo-text-secondary dark:text-gray-400 font-semibold">Total XP</p>
              <p className="font-heading font-extrabold text-4xl tracking-tight dark:text-white" data-testid="total-xp">
                {stats?.total_xp || 0}
              </p>
              <div className="space-y-1.5 mt-2">
                <div className="flex justify-between text-sm font-semibold">
                  <span className="font-heading dark:text-white" data-testid="current-level">Level {levelInfo.level}</span>
                  <span className="text-neo-text-secondary dark:text-gray-400 text-xs">{levelInfo.xpToNext} to next</span>
                </div>
                <div className="neo-progress" style={{ height: "10px" }}>
                  <div className="neo-progress-fill" style={{ width: `${levelInfo.progress}%`, height: "100%" }} data-testid="level-progress-bar" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-3 neo-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-lg bg-neo-red border-2 border-neo-border shadow-[2px_2px_0px_0px_#1A1A1A] flex items-center justify-center">
              <Flame className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm uppercase tracking-[0.15em] text-neo-text-secondary dark:text-gray-400 font-semibold">Streak</p>
              <p className="font-heading font-extrabold text-4xl tracking-tight dark:text-white" data-testid="current-streak">
                {stats?.current_streak || 0}
              </p>
            </div>
          </div>
          <p className="text-sm text-neo-text-secondary dark:text-gray-400 mt-2">
            Longest: <span className="font-bold dark:text-white" data-testid="longest-streak">{stats?.longest_streak || 0} days</span>
          </p>
        </div>
        <div className="sm:col-span-2 lg:col-span-4 neo-card">
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-neo-green border-2 border-neo-border shadow-[2px_2px_0px_0px_#1A1A1A] flex items-center justify-center mx-auto mb-2">
                <BookOpen className="w-5 h-5 text-neo-text" />
              </div>
              <p className="font-heading font-extrabold text-xl dark:text-white" data-testid="papers-completed">{stats?.papers_completed || 0}</p>
              <p className="text-xs uppercase tracking-[0.15em] text-neo-text-secondary dark:text-gray-400 font-semibold">Papers</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-neo-blue border-2 border-neo-border shadow-[2px_2px_0px_0px_#1A1A1A] flex items-center justify-center mx-auto mb-2">
                <Brain className="w-5 h-5 text-neo-text" />
              </div>
              <p className="font-heading font-extrabold text-xl dark:text-white" data-testid="total-pages-read">{stats?.total_pages_read || 0}</p>
              <p className="text-xs uppercase tracking-[0.15em] text-neo-text-secondary dark:text-gray-400 font-semibold">Pages</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-neo-purple border-2 border-neo-border shadow-[2px_2px_0px_0px_#1A1A1A] flex items-center justify-center mx-auto mb-2">
                <Clock className="w-5 h-5 text-neo-text" />
              </div>
              <p className="font-heading font-extrabold text-xl dark:text-white" data-testid="total-reading-time">
                {(() => {
                  const s = stats?.total_reading_seconds || 0;
                  const h = Math.floor(s / 3600);
                  const m = Math.floor((s % 3600) / 60);
                  return h > 0 ? `${h}h${m}m` : `${m}m`;
                })()}
              </p>
              <p className="text-xs uppercase tracking-[0.15em] text-neo-text-secondary dark:text-gray-400 font-semibold">Time</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scholar Journey Timeline */}
      <AvatarTimeline level={levelInfo.level} />

      {/* Monthly Summary Link */}
      <button
        onClick={() => navigate("/monthly-summary")}
        className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] neo-card-hover w-full text-left flex items-center gap-4 border-neo-blue border-[3px]"
        data-testid="monthly-summary-link"
      >
        <div className="w-12 h-12 rounded-lg bg-neo-blue border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] flex items-center justify-center flex-shrink-0">
          <CalendarDays className="w-6 h-6 text-neo-text" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-bold dark:text-white">Monthly Reading Summary</h3>
          <p className="text-sm text-neo-text-secondary dark:text-gray-400">
            AI-powered insights on your reading progress this month
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-neo-text-secondary dark:text-gray-400 flex-shrink-0" />
      </button>

      {/* Search & Filter Bar */}
      <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444]">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neo-text-secondary dark:text-gray-400" />
            <Input
              placeholder="Search papers by title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg py-2 focus:ring-0 focus:border-[3px] focus:border-neo-yellow"
              data-testid="search-papers-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] dark:text-white flex items-center gap-2 whitespace-nowrap" data-testid="type-filter-trigger">
                  <Filter className="w-4 h-4" />
                  {typeFilter ? typeFilter.charAt(0).toUpperCase() + typeFilter.slice(1) : "All Types"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="border-2 border-neo-border dark:border-[#444] shadow-[3px_3px_0px_0px_#1A1A1A]">
                <DropdownMenuItem onClick={() => setTypeFilter("")} data-testid="filter-all-types">All Types</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTypeFilter("empirical")} data-testid="filter-empirical">Empirical</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTypeFilter("conceptual")} data-testid="filter-conceptual">Conceptual</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTypeFilter("review")} data-testid="filter-review">Review</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTypeFilter("meta-analysis")} data-testid="filter-meta-analysis">Meta-Analysis</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ShareProgress api={api} />
          </div>
        </div>
      </div>

      {/* Review Due Card */}
      {reviewStats && reviewStats.due_today > 0 && (
        <button
          onClick={() => navigate("/review")}
          className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] neo-card-hover w-full text-left flex items-center gap-4 border-neo-purple border-[3px]"
          data-testid="review-due-card"
        >
          <div className="w-12 h-12 rounded-lg bg-neo-purple border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] flex items-center justify-center flex-shrink-0">
            <Brain className="w-6 h-6 text-neo-text" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold dark:text-white">
              {reviewStats.due_today} Review Card{reviewStats.due_today > 1 ? "s" : ""} Due
            </h3>
            <p className="text-sm text-neo-text-secondary dark:text-gray-400">
              Reinforce what you've learned. Earn +10 XP per card!
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-neo-text-secondary dark:text-gray-400 flex-shrink-0" />
        </button>
      )}

      {/* Continue Reading Section */}
      {inProgressPapers.length > 0 && (
        <div>
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight mb-4 dark:text-white">Continue Reading</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {inProgressPapers.map((paper, i) => (
              <div
                key={paper.id}
                className="neo-card neo-card-hover text-left w-full"
                data-testid={`continue-paper-${paper.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/reader/${paper.id}`)}>
                    <h3 className="font-heading font-bold text-lg truncate dark:text-white">{paper.title}</h3>
                  </div>
                  <button
                    onClick={(e) => handleDeletePaper(paper.id, e)}
                    className="neo-btn bg-white dark:bg-[#2A2A2A] dark:border-[#444] p-1.5 ml-2 flex-shrink-0"
                    data-testid={`delete-paper-${paper.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-neo-red" />
                  </button>
                </div>
                <div className="space-y-2 cursor-pointer" onClick={() => navigate(`/reader/${paper.id}`)}>
                  <div className="flex justify-between text-sm">
                    <span className="text-neo-text-secondary dark:text-gray-400">Progress</span>
                    <span className="font-bold dark:text-white">{paper.current_page + 1}/{paper.total_pages} pages</span>
                  </div>
                  <div className="neo-progress">
                    <div className="neo-progress-fill" style={{ width: `${((paper.current_page + 1) / paper.total_pages) * 100}%` }} />
                  </div>
                  <div className="flex items-center gap-1 text-sm">
                    <Zap className="w-4 h-4 text-neo-yellow" />
                    <span className="font-bold dark:text-white">{paper.xp_earned} XP</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Papers CTA */}
      {papers.length === 0 && !searchQuery && !typeFilter && (
        <div className="neo-card text-center py-12" data-testid="empty-dashboard">
          <BookOpen className="w-16 h-16 text-neo-yellow mx-auto mb-4" />
          <h2 className="font-heading font-extrabold text-2xl mb-2 dark:text-white">Start Your Reading Journey!</h2>
          <p className="text-neo-text-secondary dark:text-gray-400 mb-6 max-w-md mx-auto">
            Upload your first journal article and earn XP as you read.
          </p>
          <button onClick={() => navigate("/upload")} className="neo-btn neo-btn-primary text-lg px-8 py-3" data-testid="upload-first-paper-btn">
            Upload Your First Paper
          </button>
        </div>
      )}

      {papers.length === 0 && (searchQuery || typeFilter) && (
        <div className="neo-card text-center py-8" data-testid="no-search-results">
          <Search className="w-12 h-12 text-neo-text-secondary dark:text-gray-500 mx-auto mb-3" />
          <p className="font-heading font-bold text-lg dark:text-white">No papers found</p>
          <p className="text-sm text-neo-text-secondary dark:text-gray-400 mt-1">Try a different search term or filter</p>
        </div>
      )}

      {/* Reading Goals */}
      <GoalsWidget api={api} />

      {/* Badges Section */}
      {badges.length > 0 && (
        <div>
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight mb-4 dark:text-white">Badges Earned</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="badges-grid">
            {badges.map((badge, i) => {
              const IconComp = ICON_MAP[badge.icon] || Star;
              return (
                <div key={badge.key} className="neo-card text-center py-4" data-testid={`badge-${badge.key}`}>
                  <div className="w-14 h-14 rounded-full border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] flex items-center justify-center mx-auto mb-2" style={{ backgroundColor: badge.color || "#FFD836" }}>
                    <IconComp className="w-7 h-7 text-neo-text" />
                  </div>
                  <p className="font-heading font-bold text-sm dark:text-white">{badge.name}</p>
                  <p className="text-xs text-neo-text-secondary dark:text-gray-400 mt-1">{badge.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
